import { z } from "zod/v4";

import { UrlMatchList } from "./url-match";

export const GeoLocation = z.object({
  city: z.string(),
  region: z.string(),
  country: z.string(),
});

export type GeoLocationDto = z.infer<typeof GeoLocation>;

export const AudienceTypeList = ["common", "custom", "advanced"] as const;
export type AudienceType = (typeof AudienceTypeList)[number];

export const AudienceFilterTypeList = [
  // utm and url both mean url query params
  "utm",
  "url",
  "urlPath",
  "device",
  "visitor",
  "jsExpression",
  "trafficSource",
  "country",
  "referrer",
  "cookie",
  "klaviyo",
  "landingPage",
] as const;
export type AudienceFilterType = (typeof AudienceFilterTypeList)[number];

export const ExpressionTypeList = ["common", "custom"] as const;

export type ExpressionType = (typeof ExpressionTypeList)[number];

export const ExpressionNodeOperator = z.enum([
  "and",
  "or",
  "start_paren",
  "end_paren",
]);

export const ExpressionNodeQuery = z.object({
  key: z
    .string()
    .describe("Parameter name to match (e.g., 'utm_source', 'device_type')"),
  value: z.string().describe("Value to match against"),
  filter: z
    .enum(UrlMatchList)
    .nullish()
    .describe("Match type: 'simple', 'contains', 'regex', etc."),
  type: z
    .enum(AudienceFilterTypeList)
    .default("utm")
    .describe("Type of parameter being matched"),
});

export type ExpressionNodeQueryType = z.infer<typeof ExpressionNodeQuery>;

export const ExpressionNode = z
  .object({
    query: ExpressionNodeQuery.optional(),
    operator: ExpressionNodeOperator.optional(),
  })
  .refine(
    (val) => (val.operator && !val.query) || (!val.operator && val.query),
    { message: "must have one of 'operator' or 'query', and not both" }
  );

export type ExpressionNodeType = z.infer<typeof ExpressionNode>;

export const AudienceActionList = [
  "experienceInclude",
  "experienceExclude",
  "experienceUnassigned",
  "assignVariation",
  "randomVariation",
  "leaveUnassigned",
] as const;
export type AudienceAction = (typeof AudienceActionList)[number];

export const AudienceExcludeCurrencyDtoSchema = z.object({
  exclude: z.boolean().describe("Whether to exclude users based on currency"),
  currency: z
    .string()
    .nullable()
    .describe("Currency code to exclude (e.g., 'USD')"),
  country: z.string().optional().describe("Country to exclude"),
  region: z.string().optional().describe("Region/state to exclude"),
  city: z.string().optional().describe("City to exclude"),
});
export type AudienceExcludeCurrencyDto = z.infer<
  typeof AudienceExcludeCurrencyDtoSchema
>;

export const AudienceFilterDtoSchema = z.object({
  id: z.uuid().describe("Unique identifier for the filter"),
  priority: z
    .number()
    .int()
    .describe(
      "Priority order when multiple audiences match (lower = higher priority)"
    ),
  action: z
    .enum(AudienceActionList)
    .nullable()
    .describe(
      "What to do when audience matches: 'assignVariation', 'randomVariation', 'excludeFromExperience'"
    ),
  filterType: z
    .enum(AudienceFilterTypeList)
    .describe(
      "Type of filter: 'utm' (campaign params), 'url' (page URL), 'device', 'location', etc."
    ),
  expression: z
    .array(ExpressionNode)
    .optional()
    .nullable()
    .describe("Complex filter expression tree for advanced targeting"),
  expressionType: z
    .enum(ExpressionTypeList)
    .nullish()
    .describe("Whether using 'common' or 'custom' expression type"),
  variationId: z
    .uuid()
    .nullish()
    .describe("Specific variation to assign if action is 'assignVariation'"),
  excludeFromAnalytics: z
    .boolean()
    .default(false)
    .describe("Whether to exclude this audience from analytics tracking"),
});
export type AudienceFilterDto = z.infer<typeof AudienceFilterDtoSchema>;

export const AudienceDtoSchema = z.object({
  id: z.uuid().describe("Unique identifier for the audience"),
  experienceId: z.uuid().describe("Experience this audience belongs to"),
  enabled: z.boolean().describe("Whether audience targeting is enabled"),
  excludeCurrency: AudienceExcludeCurrencyDtoSchema.describe(
    "Currency-based exclusion rules"
  ),
  wholesale: z
    .boolean()
    .describe("Whether to include/exclude wholesale customers"),
  audienceType: z
    .enum(AudienceTypeList)
    .optional()
    .describe("Type of audience: 'common', 'custom', or 'advanced'"),
  elseAction: z
    .enum(AudienceActionList)
    .nullable()
    .describe("Default action when no filters match"),
  elseVariationId: z
    .uuid()
    .nullish()
    .describe("Default variation ID when elseAction is 'assignVariation'"),
  elseExcludeFromAnalytics: z
    .boolean()
    .default(false)
    .describe("Whether to exclude non-matching users from analytics"),
  filters: AudienceFilterDtoSchema.array()
    .optional()
    .default([])
    .describe("Array of audience filter rules evaluated in priority order"),
  evaluationFrequency: z
    .number()
    .optional()
    .nullish()
    .describe("How often to re-evaluate audience rules (in seconds)"),
});
export type AudienceDto = z.infer<typeof AudienceDtoSchema>;

export const CreateAudienceDtoSchema = AudienceDtoSchema.extend({
  experienceId: z
    .uuid()
    .optional()
    .describe("Experience ID (optional when creating with experience)"),
});
export type CreateAudienceDto = z.infer<typeof CreateAudienceDtoSchema>;

export const EXPERIENCE_AUDIENCE_ACTION_OPTIONS = {
  assignVariation: {
    title: "Assign to specific test group",
    description: "Visitor will be assigned to a specific test group.",
  },
  randomVariation: {
    title: "Assign randomly",
    description:
      "Visitor will be assigned to a test group randomly based on the %’s defined.",
  },
  experienceExclude: {
    title: "Exclude from test",
    description:
      "Visitor will be excluded from this test for the entire duration.",
  },
  experienceUnassigned: {
    title: "Do not assign",
    description:
      "Visitor will not be included yet in the test but may be subsequently included.",
  },
};

export const CAMPAIGN_EXPERIENCE_AUDIENCE_ACTION_OPTIONS = {
  experienceInclude: {
    title: "Include in Campaign",
    description: "Visitor will be included in this campaign.",
  },
  experienceExclude: {
    title: "Exclude from campaign",
    description:
      "Visitor will be excluded from this campaign for the entire duration.",
  },
  experienceUnassigned: {
    title: "Do not assign",
    description:
      "Visitor will not be included yet in the campaign but may be subsequently included.",
  },
};
